import { useMemo } from "react";
import { PatientInformationDrawerProps } from "../PatientInformationDrawer";
import getAppointmentsPreviewGridPropsProps from "./getAppointmentsPreviewGridProps";
import getPatientFormsPreviewGridProps from "./getPatientFormsPreviewGridProps";
import getPatientInformationCardProps from "./getPatientInformationCardProps";
import { Patient } from "./types";
import usePatientEditFormContainer from "./usePatientEditFormContainer";

const useNormalizedPatientDrawerData = (params: {
  patient: Patient | null;
  isEmailShown: boolean;
  isPhoneShown: boolean;
  handleCustomerClick: (customerId: string) => void;
  handleAppointmentClick: (appointmentId: string) => void;
}) => {
  const {
    patient,
    isEmailShown,
    isPhoneShown,
    handleCustomerClick,
    handleAppointmentClick,
  } = params;

  const patientEditFormContainerProps = usePatientEditFormContainer({
    patientId: patient?.id,
    isEmailShown,
    isPhoneShown,
  });

  const data = useMemo(() => {
    if (!patient || !patientEditFormContainerProps) return null;

    const result: PatientInformationDrawerProps["data"] = {
      name: `${patient.last_name}, ${patient.first_name}`,
      mergeStatus: patient.merge_status,
      patientInformationCardProps: getPatientInformationCardProps({
        patient,
        handleCustomerClick,
      }),
      appointmentsPreviewGridPropsProps: getAppointmentsPreviewGridPropsProps({
        patient,
        handleAppointmentClick,
      }),
      patientEditFormContainerProps,
      patientFormsPreviewGridProps: getPatientFormsPreviewGridProps(patient),
      auditLogsData: {
        // Interleaved by date — both sources come pre-sorted DESC on their
        // own, but need a single re-sort once combined (mirrors
        // useNormalizedCustomerDrawerData.ts).
        auditLogs: [
          ...patient.patient_audit_logs,
          ...patient.merge_audit_logs,
        ].sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        ),
        timezone:
          patient.appointment_patient_details[0]?.appointment.center.timezone,
      },
    };

    return result;
  }, [
    patient,
    patientEditFormContainerProps,
    handleCustomerClick,
    handleAppointmentClick,
  ]);

  return data;
};

export default useNormalizedPatientDrawerData;
